import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Search, 
  Check, 
  X, 
  Eye,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Edit,
  CreditCard
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function AdminMembros() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('recent');
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({});
  const queryClient = useQueryClient();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['members'],
    queryFn: () => base44.entities.Member.list('-created_date', 2000)
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.list()
  });

  const { data: ministries = [] } = useQuery({
    queryKey: ['ministries'],
    queryFn: () => base44.entities.Ministry.list()
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Member.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      toast.success('Membro atualizado com sucesso!');
      setSelectedMember(null);
      setEditingMember(null);
    },
    onError: (error) => {
      toast.error(error?.message || 'Erro ao atualizar membro.');
    },
  });

  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      member.email?.toLowerCase().includes(search.toLowerCase()) ||
      member.phone?.includes(search);
    
    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (sortOrder === 'recent') return 0;
    const nameA = (a.full_name || '').trim();
    const nameB = (b.full_name || '').trim();
    if (!nameA && !nameB) return 0;
    if (!nameA) return 1;
    if (!nameB) return -1;
    const comparison = nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
    return sortOrder === 'name-asc' ? comparison : -comparison;
  });

  const pendingMembers = members.filter(m => m.status === 'Pendente');
  const pendingCardRequests = members.filter(
    (m) => m.member_card_requested_at && m.member_card_enabled !== true
  );

  const statusColors = {
    'Pendente': 'bg-amber-100 text-amber-700',
    'Ativo': 'bg-green-100 text-green-700',
    'Inativo': 'bg-gray-100 text-gray-700',
    'Transferido': 'bg-blue-100 text-blue-700',
    'Falecido': 'bg-slate-100 text-slate-700',
    'Rejeitado': 'bg-red-100 text-red-700'
  };

  const handleApprove = (member) => {
    updateMutation.mutate({ id: member.id, data: { status: 'Ativo' } });
  };

  const handleReject = (member) => {
    updateMutation.mutate({ id: member.id, data: { status: 'Rejeitado' } });
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group?.name || '-';
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      full_name: member.full_name || '',
      cpf: member.cpf || '',
      birth_date: member.birth_date || '',
      age: member.age || '',
      phone: member.phone || '',
      email: member.email || '',
      gender: member.gender || '',
      marital_status: member.marital_status || '',
      cep: member.cep || '',
      address: member.address || '',
      address_number: member.address_number || '',
      complement: member.complement || '',
      neighborhood: member.neighborhood || '',
      city: member.city || '',
      state: member.state || '',
      congregation: member.congregation || '',
      membership_date: member.membership_date || '',
      baptism_date: member.baptism_date || '',
      status: member.status || '',
      ministries: member.ministries || [],
      group_id: member.group_id || '__none__',
      observations: member.observations || '',
      member_card_enabled: member.member_card_enabled || false,
      member_card_role: member.member_card_role || '',
      member_card_issued_at: member.member_card_issued_at || '',
      member_card_expires_at: member.member_card_expires_at || '',
      member_card_requested_at: member.member_card_requested_at || '',
      member_card_request_note: member.member_card_request_note || ''
    });
  };

  const handleSaveEdit = () => {
    const wasCardEnabled = !!editingMember.member_card_enabled;
    const willEnableCard = !!formData.member_card_enabled;
    const today = new Date();
    const nextYear = new Date(today);
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    const toDateInput = (date) => date.toISOString().slice(0, 10);
    const nullableDate = (value) => String(value || '').trim() || null;
    const payload = {
      ...formData,
      email: String(formData.email || '').trim().toLowerCase(),
      group_id: formData.group_id === '__none__' ? null : formData.group_id,
      member_card_role: String(formData.member_card_role || '').trim() || null,
      birth_date: nullableDate(formData.birth_date),
      membership_date: nullableDate(formData.membership_date),
      baptism_date: nullableDate(formData.baptism_date),
      congregation: String(formData.congregation || '').trim() || null,
      member_card_issued_at: nullableDate(formData.member_card_issued_at),
      member_card_expires_at: nullableDate(formData.member_card_expires_at),
      member_card_requested_at: nullableDate(formData.member_card_requested_at),
      member_card_request_note: String(formData.member_card_request_note || '').trim() || null,
    };
    if (willEnableCard && !wasCardEnabled) {
      payload.member_card_issued_at = toDateInput(today);
      payload.member_card_expires_at = toDateInput(nextYear);
      payload.member_card_requested_at = null;
      payload.member_card_request_note = null;
    }
    if (!willEnableCard) {
      payload.member_card_issued_at = null;
      payload.member_card_expires_at = null;
    }
    updateMutation.mutate({ id: editingMember.id, data: payload });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Membros" subtitle={`${members.length} cadastrados`} showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Pending Alert */}
        {pendingMembers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-amber-800">Aprovação Pendente</h4>
                <p className="text-sm text-amber-600">{pendingMembers.length} membro(s) aguardando</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700"
                onClick={() => setStatusFilter('Pendente')}
              >
                Ver
              </Button>
            </div>
          </motion.div>
        )}

        {pendingCardRequests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="font-medium text-indigo-900">Solicitações de Cartão</h4>
                <p className="text-sm text-indigo-700">
                  {pendingCardRequests.length} membro(s) solicitaram acesso ao cartão.
                </p>
              </div>
              <CreditCard className="w-6 h-6 text-indigo-600" />
            </div>
          </motion.div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por nome, email ou telefone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-32" aria-label="Filtrar por status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="Pendente">Pendente</SelectItem>
              <SelectItem value="Ativo">Ativo</SelectItem>
              <SelectItem value="Rejeitado">Rejeitado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="w-full sm:w-44" aria-label="Ordenar membros">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Mais recentes</SelectItem>
              <SelectItem value="name-asc">Nome A–Z</SelectItem>
              <SelectItem value="name-desc">Nome Z–A</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Members List */}
        {filteredMembers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum membro encontrado"
            description="Tente ajustar os filtros de busca."
          />
        ) : (
          <div className="space-y-3">
            {filteredMembers.map((member, index) => {
              const initials = member.full_name
                ?.split(' ')
                .map(n => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase() || 'M';

              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-white rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={member.photo_url} />
                      <AvatarFallback className="bg-indigo-100 text-indigo-600">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 truncate">{member.full_name}</h3>
                        <Badge className={statusColors[member.status]}>{member.status}</Badge>
                      </div>
                      <p className="text-sm text-gray-500 truncate">{member.email}</p>
                      {member.member_card_requested_at && member.member_card_enabled !== true && (
                        <Badge className="mt-1 bg-indigo-100 text-indigo-700">
                          Solicitou cartão
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    {member.status === 'Pendente' && (
                      <>
                        <Button
                          size="sm"
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(member)}
                          disabled={updateMutation.isPending}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Aprovar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-red-600 border-red-200"
                          onClick={() => handleReject(member)}
                          disabled={updateMutation.isPending}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Rejeitar
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedMember(member)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(member)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Member Detail Modal */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4"
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl w-full max-w-lg p-6 max-h-[85vh] overflow-auto"
            >
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" />

              <div className="flex items-center gap-4 mb-6">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selectedMember.photo_url} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xl">
                    {selectedMember.full_name?.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900 break-words">{selectedMember.full_name}</h2>
                  <Badge className={statusColors[selectedMember.status]}>{selectedMember.status}</Badge>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">CPF</p>
                    <p className="font-medium">{selectedMember.cpf || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Idade</p>
                    <p className="font-medium">{selectedMember.age || '-'} anos</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{selectedMember.phone}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span className="min-w-0 break-all">{selectedMember.email}</span>
                </div>

                {selectedMember.address && (
                  <div className="flex items-start gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 mt-1" />
                    <span>
                      {selectedMember.address}, {selectedMember.address_number}
                      {selectedMember.complement && `, ${selectedMember.complement}`}
                      <br />
                      {selectedMember.neighborhood} - {selectedMember.city}/{selectedMember.state}
                      <br />
                      CEP: {selectedMember.cep}
                    </span>
                  </div>
                )}

                {selectedMember.membership_date && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Membro desde {format(new Date(selectedMember.membership_date), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-gray-600">
                  <CreditCard className="w-4 h-4" />
                  <span>
                    Cartão de membro: {selectedMember.member_card_enabled ? 'liberado' : 'bloqueado'}
                  </span>
                </div>

                {selectedMember.member_card_requested_at && selectedMember.member_card_enabled !== true && (
                  <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3">
                    <p className="text-sm font-semibold text-indigo-900">Solicitação de cartão pendente</p>
                    <p className="text-sm text-indigo-700 mt-1">
                      {selectedMember.member_card_request_note || 'O membro solicitou acesso ao cartão.'}
                    </p>
                  </div>
                )}

                {selectedMember.ministries?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Ministérios</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedMember.ministries.map((m, i) => (
                        <Badge key={i} variant="secondary">{m}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedMember.group_id && (
                  <div>
                    <p className="text-sm text-gray-500">Grupo</p>
                    <p className="font-medium">{getGroupName(selectedMember.group_id)}</p>
                  </div>
                )}

                {selectedMember.observations && (
                  <div>
                    <p className="text-sm text-gray-500">Observações</p>
                    <p className="text-gray-600">{selectedMember.observations}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-6">
                {selectedMember.status === 'Pendente' && (
                  <>
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(selectedMember)}
                      disabled={updateMutation.isPending}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Aprovar
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200"
                      onClick={() => handleReject(selectedMember)}
                      disabled={updateMutation.isPending}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Rejeitar
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setSelectedMember(null);
                    handleEdit(selectedMember);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSelectedMember(null)}
                >
                  Fechar
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Member Dialog */}
      <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Editar Membro</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome Completo *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </div>

              <div>
                <Label>CPF</Label>
                <Input
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>

              <div>
                <Label>Data de Nascimento</Label>
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                />
              </div>

              <div>
                <Label>Idade</Label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                />
              </div>

              <div>
                <Label>Telefone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="col-span-2">
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <Label>Gênero</Label>
                <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Masculino">Masculino</SelectItem>
                    <SelectItem value="Feminino">Feminino</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Estado Civil</Label>
                <Select value={formData.marital_status} onValueChange={(v) => setFormData({ ...formData, marital_status: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Solteiro(a)">Solteiro(a)</SelectItem>
                    <SelectItem value="Casado(a)">Casado(a)</SelectItem>
                    <SelectItem value="Divorciado(a)">Divorciado(a)</SelectItem>
                    <SelectItem value="Viúvo(a)">Viúvo(a)</SelectItem>
                    <SelectItem value="União Estável">União Estável</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>CEP</Label>
                <Input
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                />
              </div>

              <div>
                <Label>Endereço</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div>
                <Label>Número</Label>
                <Input
                  value={formData.address_number}
                  onChange={(e) => setFormData({ ...formData, address_number: e.target.value })}
                />
              </div>

              <div>
                <Label>Complemento</Label>
                <Input
                  value={formData.complement}
                  onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
                />
              </div>

              <div>
                <Label>Bairro</Label>
                <Input
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                />
              </div>

              <div>
                <Label>Cidade</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>

              <div>
                <Label>Estado</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  maxLength={2}
                />
              </div>

              <div className="col-span-2">
                <Label>Congregação</Label>
                <Select value={formData.congregation} onValueChange={(v) => setFormData({ ...formData, congregation: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SEDE">SEDE</SelectItem>
                    <SelectItem value="PARQUE BRASÍLIA">PARQUE BRASÍLIA</SelectItem>
                    <SelectItem value="BAIRRO DE LOURDES">BAIRRO DE LOURDES</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Data de Membresia</Label>
                <Input
                  type="date"
                  value={formData.membership_date}
                  onChange={(e) => setFormData({ ...formData, membership_date: e.target.value })}
                />
              </div>

              <div>
                <Label>Data de Batismo</Label>
                <Input
                  type="date"
                  value={formData.baptism_date}
                  onChange={(e) => setFormData({ ...formData, baptism_date: e.target.value })}
                />
              </div>

              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pendente">Pendente</SelectItem>
                    <SelectItem value="Ativo">Ativo</SelectItem>
                    <SelectItem value="Rejeitado">Rejeitado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Grupo</Label>
                <Select value={formData.group_id} onValueChange={(v) => setFormData({ ...formData, group_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Observações</Label>
                <Textarea
                  value={formData.observations}
                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="col-span-2 rounded-xl border border-indigo-100 bg-indigo-50 p-4 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="text-gray-900">Liberar Cartão de Membro</Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Quando liberado pela primeira vez, a validade é gerada automaticamente por 1 ano.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, member_card_enabled: !formData.member_card_enabled })}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${formData.member_card_enabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`mbv-admin-switch-thumb inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white shadow transition-transform ${formData.member_card_enabled ? 'translate-x-5' : 'translate-x-0.5'}`}
                    />
                  </button>
                </div>

                <div>
                  <Label>Cargo no cartão</Label>
                  <Input
                    value={formData.member_card_role}
                    onChange={(e) => setFormData({ ...formData, member_card_role: e.target.value })}
                    placeholder="Ex: Membro, Obreiro, Diácono, Líder"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setEditingMember(null)}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending}
              >
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
